import React, { useRef, useState } from 'react';
import type { PAGE_POSITIONS } from 'types';
import type { BannerCarouselSlide } from 'types/capi/modules';
import type { Affiliate } from 'types/capi/shared';

import Image from '~/components/Image';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import AffiliatePageLink from '../components/AffiliatePageLink';
import DiscountCode from '../components/DiscountCode';

type Props = {
  buttonName?: TrackedButtonClickName;
  contentClassName?: string;
  pagePosition?: PAGE_POSITIONS;
  slides: BannerCarouselSlide[];
};

export default function BannerCarousel({
  contentClassName,
  slides,
  pagePosition,
  buttonName
}: Props) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const startX = useRef(0);

  const goToNextSlide = () => {
    setCurrentSlide((i) => (i + 1) % slides.length);
  };

  const goToPrevSlide = () => {
    setCurrentSlide((i) => (i + slides.length - 1) % slides.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0]!.clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const endX = e.changedTouches[0]!.clientX;
    const deltaX = startX.current - endX;

    if (deltaX > 50) {
      goToNextSlide();
    } else if (deltaX < -50) {
      goToPrevSlide();
    }
  };

  return (
    <div className="relative overflow-hidden">
      <div
        className="flex h-[360px] transition-transform duration-500"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {slides.map((slide, i) => (
          <div key={`splash-slide-${i}`} className="grid min-w-full">
            <BackgroundImage slide={slide} />
            <div
              className={`content-width flex items-end grid-item-stack sm:items-center ${slides.length > 1 ? 'pb-10' : 'pb-6'}`}
            >
              <div
                className={`flex w-full flex-col justify-between gap-2 lg:flex-row lg:items-end ${contentClassName}`}
              >
                <Description slide={slide} />
                <Cta
                  slide={slide}
                  pagePosition={pagePosition}
                  buttonName={buttonName}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Navigation
        numOfSlides={slides.length}
        currentSlide={currentSlide}
        onClick={setCurrentSlide}
      />
    </div>
  );
}

function BackgroundImage({ slide }: { slide: BannerCarouselSlide }) {
  return (
    <>
      <Image
        src={slide.background_image.url}
        alt={slide.background_image.alt}
        display="full-bleed"
        className="h-[360px] w-full object-cover object-right-bottom grid-item-stack"
        responsiveOptions={{
          md: { src: slide.background_image_desktop.url },
          xs: { src: slide.background_image.url }
        }}
        progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
      />
    </>
  );
}

function Description({ slide }: { slide: BannerCarouselSlide }) {
  return (
    <div className="flex flex-col gap-3 text-white">
      <div className="flex items-center gap-4">
        <AffiliateLogo affiliate={slide.cta.affiliate} className="w-14" />
        <p className="text-base tracking-wide">{slide.header}</p>
      </div>
      <p className="text-title-xl whitespace-pre-line sm:max-w-[648px]">
        {slide.description}
      </p>
    </div>
  );
}

function Cta({
  slide,
  pagePosition,
  buttonName
}: {
  buttonName?: TrackedButtonClickName;
  pagePosition?: PAGE_POSITIONS;
  slide: BannerCarouselSlide;
}) {
  return (
    <div className="flex size-full flex-col content-between justify-end lg:max-w-96">
      <div className="flex w-full flex-col gap-2 lg:max-w-96">
        <DiscountCode {...slide.discount_code} />
        <AffiliatePageLink
          affiliate={slide.cta.affiliate}
          urlTemplates={slide.cta}
          variant="forest"
          className="flex h-12 items-center justify-center gap-2"
          pagePosition={pagePosition}
          buttonName={buttonName}
        >
          <AffiliateLogo affiliate={slide.cta.affiliate} className="w-7" />
          {slide.cta.text}
        </AffiliatePageLink>
      </div>
    </div>
  );
}

function Navigation({
  numOfSlides,
  currentSlide,
  onClick
}: {
  currentSlide: number;
  numOfSlides: number;
  onClick: (i: number) => void;
}) {
  if (numOfSlides < 2) return null;

  return (
    <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2">
      {Array.from({ length: numOfSlides }).map((_, index) => (
        <button
          key={`splash-nav-button-${index}`}
          className={`size-2 rounded-full ${currentSlide === index ? 'bg-white' : 'bg-grey-hide'}`}
          onClick={() => onClick(index)}
        />
      ))}
    </div>
  );
}

function AffiliateLogo({
  affiliate,
  className = ''
}: {
  affiliate: Affiliate;
  className?: string;
}) {
  return (
    <Image
      src={affiliate.logo}
      className={`rounded bg-white ${className}`}
      alt={affiliate.name}
      loading="lazy"
      height={150}
      width={150}
      responsiveOptions={{ sm: { height: 150, pad: true, width: 150 } }}
      pad
    />
  );
}
